/**
 * confidenceEngine.js
 * Calculates Kevia's contextual confidence before responding.
 *
 * Factors:
 *   - Message length and clarity
 *   - Intent definiteness
 *   - Denominational knowledge availability
 *   - Bible retrieval signal
 *   - Pastoral risk (uncertainty under pressure)
 *   - Entity context availability
 */

/**
 * @param {{
 *   text:               string,
 *   intent:             string,
 *   bibleQuery:         object | null,
 *   denominationalPlan: object | null,
 *   ethics:             object,
 *   entityContext:      object | null,
 * }} params
 * @returns {{
 *   confidence:                    number,
 *   confidenceLabel:               "low" | "moderate" | "high",
 *   shouldQualifyResponse:         boolean,
 *   shouldAskClarifyingQuestion:   boolean,
 *   shouldAvoidDefinitiveLanguage: boolean,
 * }}
 */
export function calculateConfidence({
  text,
  intent,
  bibleQuery,
  denominationalPlan,
  ethics,
  entityContext,
}) {
  let score = 0.70; // base

  // ── Boost factors ──────────────────────────────────────────────────

  // Clear intent (not "general")
  if (intent && intent !== "general") score += 0.08;

  // Sufficient message length (a well-formed question)
  if (text?.length > 40) score += 0.05;

  // Bible reference found — direct lookup possible
  if (bibleQuery?.detected && bibleQuery.reference) score += 0.08;

  // Denominational knowledge will be available
  if (denominationalPlan?.sourceTypes?.length > 0) score += 0.05;

  // Entity context available (more grounded response)
  if (entityContext?.id) score += 0.05;

  // ── Penalty factors ────────────────────────────────────────────────

  // Very short message — ambiguous intent
  if (text?.length < 15) score -= 0.20;

  // Vague "general" intent with no context
  if (intent === "general" && !entityContext) score -= 0.10;

  // High/critical risk — epistemic caution needed
  const RISK_PENALTY = { none: 0, low: 0, medium: 0.05, high: 0.12, critical: 0.15 };
  score -= RISK_PENALTY[ethics?.riskLevel ?? "none"] ?? 0;

  // Sermon/reflection with no Bible retrieval — uncertain content
  if ((intent === "sermon" || intent === "reflection") && !bibleQuery?.detected) {
    score -= 0.05;
  }

  // Discipline or doctrinal caution → qualify
  if (ethics?.requiresDoctrinalCaution) score -= 0.08;

  score = Math.max(0.10, Math.min(1.0, score));

  const confidenceLabel =
    score >= 0.80 ? "high" :
    score >= 0.55 ? "moderate" :
    "low";

  const shouldQualifyResponse         = score < 0.70 || ethics?.requiresDoctrinalCaution;
  const shouldAskClarifyingQuestion   = score < 0.50 && text?.length < 20;
  const shouldAvoidDefinitiveLanguage = score < 0.65 || (ethics?.riskLevel ?? "none") !== "none";

  return {
    confidence:                    Math.round(score * 100) / 100,
    confidenceLabel,
    shouldQualifyResponse,
    shouldAskClarifyingQuestion,
    shouldAvoidDefinitiveLanguage,
  };
}
